import React, { useState, useEffect } from 'react';
import { 
  ChatBubbleLeftEllipsisIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  CameraIcon,
  HeartIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  FaceSmileIcon,
  FaceFrownIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import html2canvas from 'html2canvas';
import { useAuth } from '../../contexts/AuthContext';
import { processFeedback, calculateQualityScore } from '../../utils/feedbackFilter';
import { gamificationActions } from '../../utils/gamificationSystem';
import { sendFeedbackToGitHub } from '../../utils/githubIssues';
import FeedbackImpactTrigger from './FeedbackImpactTrigger';
import HeartAnimation from './HeartAnimation';

const FloatingFeedback = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  const [qualityCheck, setQualityCheck] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [githubSendStatus, setGithubSendStatus] = useState(null); // 'sending', 'success', 'failed'
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);
  
  const { user, isAuthenticated, isBetaUser } = useAuth();

  // 페이지 스크롤시 버튼 숨김/표시 효과
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateFloatingButton = () => {
      const scrollY = window.scrollY;
      
      if (Math.abs(scrollY - lastScrollY) < 10) {
        ticking = false;
        return;
      }

      if (scrollY > lastScrollY) {
        setShowFloatingButton(false);
      } else {
        setShowFloatingButton(true);
      }

      lastScrollY = scrollY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateFloatingButton);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // 실시간 품질 체크
  useEffect(() => {
    if (feedbackText.trim().length > 2) {
      const quality = calculateQualityScore(feedbackText);
      setQualityCheck(quality);
      
      if (quality.score < 40) {
        setValidationError(quality.issues.join(' '));
      } else {
        setValidationError('');
      }
    } else {
      setQualityCheck(null);
      setValidationError('');
    }
  }, [feedbackText]);

  const moods = [
    { emoji: '😍', label: '너무 좋아요', value: 'love' },
    { emoji: '😊', label: '좋아요', value: 'good' },
    { emoji: '😐', label: '보통이에요', value: 'neutral' },
    { emoji: '😕', label: '아쉬워요', value: 'bad' },
    { emoji: '😡', label: '문제 있어요', value: 'terrible' }
  ];

  const takeScreenshot = async () => {
    try {
      setIsCapturing(true);
      
      // 모달 임시 숨김
      setIsOpen(false);
      
      // 애니메이션 완료 대기 (모달 사라질 때까지)
      await new Promise(resolve => setTimeout(resolve, 350));
      
      const canvas = await html2canvas(document.body, {
        height: window.innerHeight,
        width: window.innerWidth,
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        allowTaint: true
      });
      
      const dataUrl = canvas.toDataURL('image/png');
      setScreenshot(dataUrl);
      
      // 모달 다시 표시
      setIsOpen(true);
      
      // 캡처 성공 피드백 (짧게 표시)
      setTimeout(() => {
        const successMsg = document.createElement('div');
        successMsg.textContent = '✅ 화면이 성공적으로 캡처되었습니다!';
        successMsg.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm font-medium';
        document.body.appendChild(successMsg);
        setTimeout(() => successMsg.remove(), 2000);
      }, 100);
      
    } catch (error) {
      console.error('스크린샷 캡처 실패:', error);
      alert('스크린샷 캡처에 실패했습니다. 다시 시도해주세요.');
      // 에러 발생 시에도 모달 복구
      setIsOpen(true);
    } finally {
      setIsCapturing(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedMood || !feedbackText.trim()) {
      setValidationError('신고 유형과 구체적인 내용을 모두 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setValidationError('');

    // 피드백 데이터 구성
    const feedbackData = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      mood: selectedMood,
      text: feedbackText.trim(),
      screenshot: screenshot,
      page: window.location.pathname,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    // 사용자 정보
    const userInfo = {
      userId: user?.id,
      isAuthenticated,
      isBetaUser,
      name: user?.name
    };

    try {
      // 품질 필터링 처리
      const validation = processFeedback(feedbackData, userInfo);
      
      if (!validation.success) {
        setValidationError(validation.message);
        setIsSubmitting(false);
        
        // Rate limit 에러의 경우 특별 처리
        if (validation.error === 'RATE_LIMITED') {
          setTimeout(() => {
            setValidationError('');
          }, 5000);
        }
        return;
      }

      // localStorage에 저장 (처리된 피드백 사용)
      const existingFeedback = JSON.parse(localStorage.getItem('betaFeedback') || '[]');
      existingFeedback.push(validation.feedback);
      localStorage.setItem('betaFeedback', JSON.stringify(existingFeedback));

      // GitHub Issues로 피드백 전송 시도 (Zero-Risk: 실패해도 기존 기능에 영향 없음)
      setGithubSendStatus('sending');
      try {
        // 피드백을 GitHub Issues 형식으로 변환
        const githubFeedback = {
          message: validation.feedback.text,
          mood: validation.feedback.mood === 'love' ? 'positive' : 
                validation.feedback.mood === 'good' ? 'positive' :
                validation.feedback.mood === 'neutral' ? 'neutral' : 'negative',
          category: validation.feedback.qualityScore > 80 ? 'content' : 'ui',
          priority: validation.feedback.mood === 'terrible' ? 'high' : 'medium',
          context: {
            page: validation.feedback.page,
            userAgent: navigator.userAgent.substring(0, 100),
            viewport: validation.feedback.viewport,
            qualityScore: validation.feedback.qualityScore,
            hasScreenshot: !!validation.feedback.screenshot,
            userId: validation.feedback.userId,
            isAuthenticated: validation.feedback.isAuthenticated,
            isBetaUser: validation.feedback.isBetaUser
          },
          timestamp: validation.feedback.timestamp,
          triggerName: 'manual_feedback',
          isExitIntent: false,
          sessionId: `session_${Date.now()}`
        };

        const githubResult = await sendFeedbackToGitHub(githubFeedback);
        
        if (githubResult && githubResult.success) {
          console.log(`✅ 피드백이 GitHub Issue #${githubResult.issueNumber}로 생성되었습니다`);
          setGithubSendStatus('success');
          
          // GitHub Issue 정보를 localStorage의 피드백에 추가
          const updatedFeedback = [...existingFeedback];
          const lastFeedback = updatedFeedback[updatedFeedback.length - 1];
          lastFeedback.githubIssue = githubResult;
          localStorage.setItem('betaFeedback', JSON.stringify(updatedFeedback));
        } else {
          setGithubSendStatus('failed');
        }
      } catch (githubError) {
        // GitHub 전송 실패
        console.log('GitHub 전송 실패:', githubError.message);
        setGithubSendStatus('failed');
      }

      // 성공 처리
      setShowThankYou(true);
      setShowHeartAnimation(true); // 하트 애니메이션 시작
      
      // 게이미피케이션: 피드백 제출 기록
      gamificationActions.recordFeedback();
      
      setTimeout(() => {
        setIsOpen(false);
        setShowThankYou(false);
        setFeedbackText('');
        setSelectedMood('');
        setScreenshot(null);
        setQualityCheck(null);
        setValidationError('');
        setGithubSendStatus(null);
        setShowHeartAnimation(false);
      }, 3000); // 3초로 연장 (하트 애니메이션 시간)

    } catch (error) {
      console.error('Failed to save feedback:', error);
      setValidationError('피드백 저장 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setFeedbackText('');
    setSelectedMood('');
    setScreenshot(null);
    setShowThankYou(false);
    setQualityCheck(null);
    setValidationError('');
    setGithubSendStatus(null);
    setShowHeartAnimation(false);
  };

  if (showThankYou) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <div className="bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl animate-bounce max-w-sm">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">{
              githubSendStatus === 'sending' ? '📤' :
              githubSendStatus === 'success' ? '🎉✨💝' :
              githubSendStatus === 'failed' ? '🎉⚠️' : '🎉'
            }</span>
            <div className="flex-1">
              <div className="font-bold text-sm">
                {githubSendStatus === 'sending' && '전송 중...'}
                {githubSendStatus === 'success' && '전송 완료! 소중한 의견 감사합니다! 💝'}
                {githubSendStatus === 'failed' && '피드백 저장됨! 감사합니다! 💝'}
                {!githubSendStatus && '피드백 감사합니다! 💝'}
              </div>
              <div className="text-xs opacity-90 mb-3">
                {githubSendStatus === 'sending' && '피드백을 전송하고 있습니다...'}
                {githubSendStatus === 'success' && '서버에 성공적으로 전송되었습니다!'}
                {githubSendStatus === 'failed' && '로컬에 저장되었습니다 (서버 연결 문제)'}
                {!githubSendStatus && '더 나은 서비스로 보답하겠습니다'}
              </div>
              
              <div className="flex flex-wrap gap-2">
                <FeedbackImpactTrigger 
                  variant="button"
                  className="bg-white text-green-600 text-xs px-3 py-1 rounded-full font-medium hover:bg-gray-100 transition-colors"
                  showLabel={true}
                />
                {isBetaUser ? (
                  <button
                    onClick={() => {
                      // 대시보드 열기 이벤트 발생
                      window.dispatchEvent(new CustomEvent('openBetaDashboard'));
                      setShowThankYou(false);
                    }}
                    className="bg-white text-green-600 text-xs px-3 py-1 rounded-full font-medium hover:bg-gray-100 transition-colors flex items-center space-x-1"
                  >
                    <ChartBarIcon className="w-3 h-3" />
                    <span>대시보드 보기</span>
                  </button>
                ) : (
                  <div className="text-xs text-green-200 px-3 py-1">
                    베타 테스터가 되어 더 많은 기능을 이용해보세요!
                  </div>
                )}
                <button
                  onClick={() => setShowThankYou(false)}
                  className="bg-green-600 text-white text-xs px-3 py-1 rounded-full font-medium hover:bg-green-700 transition-colors"
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 플로팅 피드백 버튼 */}
      {!isOpen && (
        <div className={`fixed bottom-6 right-6 z-40 transition-all duration-300 ${
          showFloatingButton ? 'translate-y-0 opacity-100 pointer-events-auto' : 'translate-y-16 opacity-0 pointer-events-none'
        }`}>
          {/* 메인 버튼 */}
          <button
            onClick={() => setIsOpen(true)}
            className="relative bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-4 py-3 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 group flex items-center space-x-2"
            title="오류신고 및 건의사항"
          >
            <ChatBubbleLeftEllipsisIcon className="w-5 h-5" />
            <span className="font-medium text-sm">오류신고</span>
            
            {/* 호버 효과를 위한 그로우 */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-700 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
          </button>
        </div>
      )}

      {/* 피드백 모달 */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* 헤더 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-3xl">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <ChatBubbleLeftEllipsisIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">오류신고 및 건의사항</h3>
                  <p className="text-sm text-orange-100">더 나은 서비스를 위해 도움을 주세요</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-white/70 hover:text-white rounded-full hover:bg-white/20 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* 콘텐츠 */}
            <div className="p-6 space-y-6">
              {/* 신고 유형 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  어떤 종류의 신고인가요?
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {moods.map((mood) => (
                    <button
                      key={mood.value}
                      onClick={() => setSelectedMood(mood.value)}
                      className={`p-3 rounded-xl text-center transition-all duration-200 transform ${
                        selectedMood === mood.value
                          ? 'bg-gradient-to-r from-orange-100 to-orange-200 border-2 border-orange-500 scale-110 shadow-lg'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 hover:scale-105'
                      }`}
                    >
                      <div className="text-2xl mb-1">{mood.emoji}</div>
                      <div className="text-xs text-gray-600">{mood.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 신고 내용 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  구체적인 내용을 알려주세요
                </label>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="• 발생한 오류의 상황을 자세히 설명해주세요&#10;• 개선했으면 하는 기능이나 문제점을 알려주세요&#10;• 어떤 페이지에서 문제가 발생했는지 알려주세요"
                  className="w-full h-32 p-4 border border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none resize-none text-sm transition-colors"
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-2">
                  <div className="flex items-center space-x-2">
                    <div className="text-xs text-gray-400">
                      {feedbackText.length}/500
                    </div>
                    {qualityCheck && (
                      <div className="flex items-center space-x-1">
                        {qualityCheck.category === 'excellent' && (
                          <>
                            <ShieldCheckIcon className="w-3 h-3 text-green-500" />
                            <span className="text-xs text-green-600 font-medium">품질 우수</span>
                          </>
                        )}
                        {qualityCheck.category === 'good' && (
                          <>
                            <ShieldCheckIcon className="w-3 h-3 text-blue-500" />
                            <span className="text-xs text-blue-600 font-medium">품질 양호</span>
                          </>
                        )}
                        {(qualityCheck.category === 'poor' || qualityCheck.category === 'spam') && (
                          <>
                            <ExclamationTriangleIcon className="w-3 h-3 text-red-500" />
                            <span className="text-xs text-red-600 font-medium">품질 개선 필요</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {qualityCheck && (
                    <div className="text-xs text-gray-500">
                      품질 점수: {qualityCheck.score}/100
                    </div>
                  )}
                </div>
                
                {/* 검증 오류 메시지 */}
                {validationError && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-red-800">피드백 검증 실패</div>
                      <div className="text-xs text-red-600 mt-1">{validationError}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* 스크린샷 */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      문제 화면 캡처 (선택사항)
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      오류가 발생한 화면을 첨부하면 빠른 해결에 도움됩니다
                    </p>
                  </div>
                  <button
                    onClick={takeScreenshot}
                    disabled={isCapturing}
                    className="flex items-center space-x-2 text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    <CameraIcon className={`w-4 h-4 ${isCapturing ? 'animate-pulse' : ''}`} />
                    <span>{isCapturing ? '화면 캡처 중...' : '화면 캡처'}</span>
                  </button>
                </div>
                {screenshot && (
                  <div className="relative">
                    <img 
                      src={screenshot} 
                      alt="Screenshot" 
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      onClick={() => setScreenshot(null)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* 푸터 */}
            <div className="p-6 border-t border-gray-100">
              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="flex-1 py-3 px-4 text-gray-600 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!selectedMood || !feedbackText.trim() || isSubmitting || (qualityCheck && qualityCheck.score < 20)}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 transform hover:scale-105"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>전송중...</span>
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-4 h-4" />
                      <span>신고 전송</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 하트 애니메이션 */}
      <HeartAnimation 
        isVisible={showHeartAnimation} 
        onComplete={() => setShowHeartAnimation(false)}
      />
    </>
  );
};

export default FloatingFeedback;